import React, { useEffect, useState } from 'react';
const FRAME_SHADOW = 'shadow-[0_28px_50px_-28px_rgba(26,40,56,0.45)] ring-1 ring-black/5';
const FRAME_SHADOW_EMPHASIS = 'shadow-[0_32px_56px_-28px_rgba(26,40,56,0.5)] ring-2 ring-seaglass/35';
interface ProofFrameProps {
  src: string;
  alt: string;
  /** Decorative chrome label — not a live URL. */
  chromeLabel: string;
  /** object-position for the homepage crop inside the fixed 3:2 frame. */
  objectPosition?: string;
  loading?: 'eager' | 'lazy';
  /** Slightly stronger frame treatment for the commercial handoff stage. */
  emphasize?: boolean;
  /**
   * Slow CSS pan top→bottom→top for tall homepage screenshots.
   * Honors prefers-reduced-motion (static top crop).
   */
  scrollReveal?: boolean;
  /** Shown instead of src when the user prefers reduced motion. */
  staticSrc?: string;
  className?: string;
}
function BrowserChrome({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-2 border-b border-gray-200/80 bg-paper-dark px-3 py-2 sm:px-4 sm:py-2.5" data-id="element-859" data-mp="s3rydwnq0w9v">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-860" data-mp="1w1jxse6yzwaz" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-861" data-mp="20zlj75rvc7pm" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-862" data-mp="25senxtnc7g1p" />
      <div className="ml-2 flex min-w-0 flex-1 select-none items-center rounded-md bg-white px-2.5 py-1 text-[0.7rem] font-medium text-text-muted sm:ml-3 sm:px-3 sm:text-xs" aria-hidden="true" data-id="element-863" data-mp="2aqg4o7xaj3a0">
        <span className="truncate" data-id="element-864" data-mp="1i33ivzyyezhr">{label}</span>
      </div>
    </div>;
}
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return reduced;
}

/**
 * Shared proof screenshot frame — identical width, 3:2 screen, chrome,
 * radius, and shadow for every homepage proof stage.
 */
export function ProofFrame({
  src,
  alt,
  chromeLabel,
  objectPosition = 'center top',
  loading = 'lazy',
  emphasize = false,
  scrollReveal = false,
  staticSrc,
  className = ''
}: ProofFrameProps) {
  const [errored, setErrored] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const displaySrc = reducedMotion && staticSrc ? staticSrc : src;
  const useScrollClass = scrollReveal && !reducedMotion;
  const pendingFile = displaySrc.split('/').pop();
  return <div className={`w-full overflow-hidden rounded-2xl border border-white/10 bg-white ${emphasize ? FRAME_SHADOW_EMPHASIS : FRAME_SHADOW} ${className}`} data-id="element-865" data-mp="tfrb6nx7rio4">
      <BrowserChrome label={chromeLabel} data-id="element-866" data-mp="omelsbkpn7ub" />
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-white" data-id="element-867" data-mp="yeu99fkrdeo">
        {!errored ? <img key={displaySrc} src={displaySrc} alt={alt} loading={loading} decoding="async" onError={() => setErrored(true)} className={useScrollClass ? 'proof-home-scroll h-full w-full' : 'h-full w-full object-cover'} style={useScrollClass ? undefined : {
        objectPosition
      }} data-id="element-868" data-mp="3t2zctxr1b7lm" /> : <div className="flex h-full w-full flex-col items-center justify-center gap-2 border border-dashed border-gray-300 bg-paper-dark p-6 text-center" data-id="element-869" data-mp="390mo6j83b7mv">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted" data-id="element-870" data-mp="3e07k0kryqzvd">
              Proof asset pending
            </p>
            {pendingFile ? <code className="max-w-full truncate rounded bg-white px-2 py-1 text-[0.7rem] text-navy ring-1 ring-gray-200" data-id="element-871" data-mp="ajinlrm3h6jf">
                {pendingFile}
              </code> : null}
          </div>}
      </div>
    </div>;
}